import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {Mail, PortalApi, WebmailApi} from "components/src/api/portal/generated";
import {Attachments} from "components/src/common/Attachments";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {FloatingLabel} from "components/src/common/FloatingLabel";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {DateUtils} from "components/src/utils/DateUtils";
import {ReplySizeUtils} from "components/src/utils/ReplySizeUtils";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {Attachment as AttachmentIcon, SendDiagonal, Trash as DeleteIcon} from "iconoir-react";
import {useEffect, useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import "./ReplyPopup.scss"

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());
const portalApi = new PortalApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface ReplyProps {
    sourceMail : Mail
    sourceBody : Array<string> | undefined
    onClose : OnClose
}

export const ReplyPopup = ({sourceMail, sourceBody, onClose} : ReplyProps) =>
{
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    /**
     * Body of the reply
     */
    const [body, setBody] = useState<string>(getInitialBody());
    /**
     * Attachments
     */
    const [attachments, setAttachments] = useState<File[]>([]);
    /*
     * Max size in bytes of all attachments
     */
    const [attachmentsMaxSize, setAttachmentsMaxSize] = useState<number | undefined>();
    /*
     * Max number of attachments, undefined if there is none
     */
    const [attachmentsMaxCount, setAttachmentsMaxCount] = useState<number | undefined>();
    /*
     * Max size of the body, in characters
     */
    const [bodyMaxSize, setBodyMaxSize] = useState<number | undefined>();
    /**
     * The error message if some error occurred, which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Set to true when the user has made any changes to the form
     */
    const [isDirty, setIsDirty] = useState<boolean>(false);

    /**
     * The reply was sent but the copy could not be filed in Sent Items. The reply is gone either way, so
     * the dialog is not closed with an error: it says what happened and can only be closed, because
     * sending again would deliver the reply twice
     */
    const [notSavedToSentItems, setNotSavedToSentItems] = useState<boolean>(false);
    /**
     * Set to true to show the discard-changes confirmation dialog
     */
    const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);
    /**
     * Controls the Modal's show prop. We drive visibility ourselves rather than
     * hardcoding show={true} so that Bootstrap can play the full close animation
     * before the parent unmounts this component. Abruptly unmounting a shown Modal
     * (by having the parent flip showReplyPopup to false while show is still true)
     * skips the animation and leaves Bootstrap's modal-stack bookkeeping in an
     * inconsistent state, preventing the popup from visually closing.
     */
    const [show, setShow] = useState<boolean>(true);
    /**
     * Holds the close cause while the hide animation is in progress. Set before
     * calling setShow(false); read in onExited once the animation completes.
     * Using a ref avoids an extra render cycle between recording the cause and
     * acting on it.
     */
    const pendingCloseCause = useRef<DialogCloseCause | null>(null);
    /**
     * Reference to the <textarea> element so it can receive focus
     */
    const bodyRef = useRef(null as HTMLTextAreaElement | null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    function getInitialBody()
    {
        let initialBody : string = "";

        if (sourceBody != undefined)
        {
            initialBody = "\n________________________________________\n";

            if (sourceMail.from != undefined) {
                initialBody += t('portal.messages.from') + ": " + sourceMail.from + "\n";
            }

            if (sourceMail.to != undefined) {
                initialBody += t('portal.messages.to') + ": " + sourceMail.to + "\n";
            }

            if (sourceMail.cc != undefined) {
                initialBody += t('portal.messages.cc') + ": " + sourceMail.cc + "\n";
            }

            if (sourceMail.date != undefined) {
                initialBody += t('portal.messages.date') + ": " + DateUtils.fromTimestampToLocaleDateString(sourceMail.date) + "\n";
            }

            if (sourceMail.subject != undefined) {
                initialBody += t('portal.messages.subject') + ": " + sourceMail.subject + "\n";
            }

            initialBody += "\n\n" + sourceBody.join("\n");
        }

        return initialBody;
    }

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        portalApi.portalControllerGetPortalProperties().then(
            (response) => {
                setAttachmentsMaxSize(response.data.replyAttachmentsMaxSize)
                setAttachmentsMaxCount(response.data.replyAttachmentsMaxCount)
                setBodyMaxSize(response.data.replyBodyMaxSize)
            }
        )
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter]);

    const sendReply = () =>
    {
        // refuse a body which is too large before the reply is uploaded, instead of after. The quoted message counts
        // as well
        const bodySizeError = ReplySizeUtils.getBodySizeError(body, bodyMaxSize, 'backend.webmail.body-too-large', t);

        if (bodySizeError !== undefined)
        {
            setErrorMessage(bodySizeError);
            return;
        }

        // reset error message
        setErrorMessage(undefined);

        setSending(true);

        webmailApi.webmailPortalControllerReply(sourceMail.folder, sourceMail.uid, body, attachments).then(response => {
            if (response.data.savedToSentItems)
            {
                // Record the cause and let the hide animation run; onExited will call onClose.
                pendingCloseCause.current = DialogCloseCause.Succes;
                setShow(false);
            }
            else {
                // sent, so nothing here may lead to sending it again
                setIsDirty(false);
                setNotSavedToSentItems(true);
            }
        })
        .catch((err: Error | AxiosError) => {
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    function addAttachments(fileList : FileList)
    {
        const newFiles = Array.from(fileList);

        const attachmentsCheckError = ReplySizeUtils.getAttachmentsError(attachments, newFiles, attachmentsMaxSize,
            attachmentsMaxCount, t);

        if (attachmentsCheckError !== undefined)
        {
            setErrorMessage(attachmentsCheckError);
            return;
        }

        // reset error message
        setErrorMessage(undefined);

        setIsDirty(true);
        setAttachments(prevAttachments => [...prevAttachments, ...newFiles]);
    }

    function removeAttachment(file : File){
        setAttachments(prevAttachments => prevAttachments.filter(f => f !== file));
    }

    // Called when the modal requests to be closed (X button or ESC key).
    const onHideDialog = () => {
        if (isDirty) {
            // Unsaved changes: ask the user to confirm before discarding.
            setShowDiscardConfirm(true);
        } else {
            // No unsaved changes: start the hide animation; onExited will call onClose.
            pendingCloseCause.current = DialogCloseCause.Cancel;
            setShow(false);
        }
    }

    return (
        <>
        <Modal show={show}
               onHide={() => onHideDialog()}
               // onExited fires after Bootstrap's hide animation completes. Notifying
               // the parent here (rather than at the moment setShow(false) is called)
               // ensures the modal is fully gone before the parent unmounts this component.
               onExited={() => { if (pendingCloseCause.current !== null) onClose(pendingCloseCause.current); }}
               backdrop="static"
               scrollable={true}
               dialogClassName={"cm-modal-dialog"}
               contentClassName={"cm-modal-content"}
               onEntered={() => {bodyRef.current?.focus()}}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('portal.messages.mail.reply-title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column">
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>

                        <RenderAlert message={notSavedToSentItems ?
                                         t('portal.messages.mail.sent-but-not-saved-to-sent-items') : undefined}
                                     variant="warning"
                                     tooltipPlacement={"bottom"}/>

                        <div className="d-flex flex-column flex-grow-1">
                            <FloatingLabel label={t('portal.messages.subject')} className="text-secondary mb-3">
                                <Form.Control type="text" value={t('portal.messages.mail.subject-prefix') + (sourceMail.subject ?? "")} placeholder={t('portal.messages.subject')}
                                              readOnly={true}/>
                            </FloatingLabel>

                            <FloatingLabel label={t('portal.messages.to')} className="text-secondary mb-3">
                                <Form.Control type="text" value={sourceMail.from ?? ""} placeholder={t('portal.messages.to')}
                                              readOnly={true}/>
                            </FloatingLabel>

                            <Form.Group className="mb-3 d-flex flex-column flex-grow-1">
                                <Form.Control
                                    className="cm-mail-textarea flex-grow-1"
                                    as="textarea"
                                    aria-label={t('portal.messages.body')}
                                    onChange={e => { setIsDirty(true); setBody(e.target.value); }}
                                    value={body}
                                    ref={bodyRef}
                                />
                            </Form.Group>

                            <Attachments
                                attachments={attachments}
                                dropdownItemText={t('portal.messages.mail.remove-attachment-title')}
                                onDropdownItemClick={(index) => removeAttachment(attachments[index])}
                                icon={<DeleteIcon className="me-1"/>}
                            />
                        </div>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <div>
                    {/*
                     * The native file input is hidden (d-none) because it cannot be
                     * styled consistently across browsers. The Button below acts as
                     * the visible trigger and programmatically opens the file picker
                     * via fileInputRef.current?.click().
                     */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple={true}
                        accept=""
                        className="d-none"
                        onChange={e => {
                            addAttachments((e.target.files as FileList));
                            // otherwise picking the same file again, after it was refused or removed, does nothing
                            e.target.value = "";
                        }}
                    />
                    <Button variant="outline-light" className="text-decoration-none text-secondary"
                            onClick={() => fileInputRef.current?.click()}>
                        <AttachmentIcon/> {t('portal.messages.mail.add-attachment-label')}
                    </Button>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary"
                            onClick={onHideDialog}>
                        {notSavedToSentItems ? t('common.close') : t('portal.messages.mail.cancel')}
                    </Button>
                    <Button variant="success"
                            onClick={sendReply}
                            disabled={notSavedToSentItems || StringUtils.isBlank(body) || spinnerStateCounter > 0}>
                        <SendDiagonal className="me-2"/>
                        {sending && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {sending ? t('portal.messages.mail.sending') : t('portal.messages.mail.send')}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>

        <ConfirmDialog
            title={t('common.confirm')}
            bodyText={t('portal.messages.mail.discard-unsaved-warning')}
            confirmButtonTitle={t('portal.messages.mail.discard-unsaved-confirm')}
            onClickConfirmEvent={() => {
                // Close the confirm dialog first so Bootstrap can cleanly unstack it
                // before the reply modal itself starts its hide animation.
                setShowDiscardConfirm(false);
                pendingCloseCause.current = DialogCloseCause.Cancel;
                setShow(false);
            }}
            showConfirm={showDiscardConfirm}
            setShowConfirm={setShowDiscardConfirm}
            buttonVariant="warning"
        />
        </>
)
}